import {
    catchError,
    map,
    mergeMap,
    Observable,
    of,
    throwError
} from "rxjs";

import { HttpClient } from "@angular/common/http";
import { RxjsUtil } from "../../../../utils/rxjs-util";
import {
    Injectable, inject
} from "@angular/core";

import {
    IRequestModel,
    ISerializeOptions,
    IValidatedDomainModel,
    IValidatedModel,
    ModelAction,
    ModelUtil
} from "@shopliftr/common-js/data";
import {
    AutocompleteRequest,
    AutocompleteResponse,
    BulkRequest,
    BulkResponse,
    CreateRequest,
    CreateResponse,
    CustomOpRequest,
    CustomOpResponse,
    DeleteRequest,
    DeleteResponse,
    GetRequest,
    GetResponse,
    RequestUtil,
    SearchRequest,
    SearchResponse,
    SubmitRequest,
    SubmitResponse,
    UpdateRequest,
    UpdateResponse
} from "@shopliftr/common-js/shared";

import { ValidationException } from "../../../../exceptions/validation-exception";
import { InvalidParametersException } from "../../../../exceptions/invalid-parameters-exception";
import { COMMON_ERROR_HANDLER } from "../../../../exceptions/constants/exception-constants";
import { INTERNAL_API_APP_CONFIG } from "../../../constants/iapi-app-tokens";

/**
 * A wrapper service which validates and pre-processes requests to Internal API via the IInternalApiClient interface.
 * This allows us to remove the error handler and client from the front-facing services and unify handling for
 * our CRUD functions.
 *
 * Example:
 *      // In DataEntryDealService
 *      delete(id: string, version: number, options?: DataEntryDealDeleteRequestOptions): Observable<DeleteResponse> {
 *
 *          const request = RequestUtil.createDeleteRequest(
 *              id,
 *              DataEntryDeal.modelName,
 *              ModelAction.ClientDelete,
 *              version,
 *              options
 *          );
 *
 *          // The base path for the endpoint relative to api url
 *          // so for https://internal-api.mygrocerydeals.com/data-entry-deal/a01dee8c-3ba0-4a98-ba24-09a6e4f0225d
 *          // the base path is "data-entry-deal"
 *          const basePath = "data-entry-deal"
 *          return this._internalApiRequestService.delete$(request, basePath);
 *      }
 */
@Injectable(
    { providedIn: "root" }
)
export class InternalApiRequestService {

    private readonly _httpClient = inject(HttpClient);
    private readonly _errorHandler = inject(COMMON_ERROR_HANDLER);
    private readonly _appConfig = inject(INTERNAL_API_APP_CONFIG);

    /**
     * Performs a Bulk Request Action with the provided request against the bulk end point
     */
    bulk$(request: BulkRequest): Observable<BulkResponse> {

        try {
            this._preprocessRequest(request);

            return this._httpClient.post(
                this._getUrl(request, "bulk"),
                request.serialize()
            ).pipe(
                map((result: unknown): BulkResponse => {

                    return BulkResponse.deserialize(result);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Performs a Create Request Action with the provided request against the provided basePath
     */
    create$<M extends IValidatedModel<M>, O extends IValidatedModel<O> | void = void>(
        request: CreateRequest<M, O>,
        basePath: string
    ): Observable<CreateResponse<M>> {

        try {
            this._preprocessRequest(request);

            return this._httpClient.post(
                this._getUrl(request, basePath),
                request.serialize()
            ).pipe(
                map((result: unknown) => {

                    return CreateResponse.deserialize<M>(result);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Performs a Delete request Action with the provided request against the provided basePath
     */
    delete$<O extends IValidatedModel<O> | void = void>(request: DeleteRequest<O>, basePath: string): Observable<DeleteResponse> {

        try {
            this._preprocessRequest(request);

            return this._httpClient.delete(
                this._getUrl(request, basePath),
                {
                    body: request.serialize()
                }
            ).pipe(
                map((result: unknown) => {

                    return DeleteResponse.deserialize(result);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Performs a Get Request Action with the provided request against the provided basePath
     */
    get$<M extends IValidatedModel<M>, O extends IValidatedModel<O> | void = void>(
        request: GetRequest<O>,
        basePath: string
    ): Observable<GetResponse<M>> {

        try {
            this._preprocessRequest(request);

            return this._httpClient.get(
                this._getUrl(request, basePath)
            ).pipe(
                map((result: unknown) => {

                    return GetResponse.deserialize<M>(result);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Calls search as many times as necessary for all of the records matching the provided criteria
     * to have been retrieved
     */
    getAll$<M extends IValidatedModel<M>, F extends IValidatedModel<F>>(request: SearchRequest<F>, path: string): Observable<Array<M>> {

        try {
            this._preprocessRequest(request);

            const PAGE_SIZE = 100;

            const results = new Array<M>();

            const requestCopy = request.clone();

            requestCopy.size = PAGE_SIZE;
            requestCopy.from = 0;

            return this.search$(requestCopy, path).pipe(
                mergeMap((interimResults: SearchResponse<M>) => {

                    // After the first search runs, store those results
                    results.push(...interimResults.results);

                    // Then, if there are no more chains to get, move on to processing immediately
                    if (interimResults.total <= PAGE_SIZE) {
                        return of([]);
                    }
                    // Otherwise, fetch the remaining pages with bounded concurrency
                    else {
                        return RxjsUtil.fetchRemainingPages$(
                            interimResults.total,
                            PAGE_SIZE,
                            (from: number) => {
                                const pageRequest = request.clone();
                                pageRequest.size = PAGE_SIZE;
                                pageRequest.from = from;

                                return this.search$<M, F>(pageRequest, path);
                            },
                            (error) => this._errorHandler.handleServiceError(error)
                        );
                    }
                }),
                map((combinedResults: Array<SearchResponse<M>>): Array<M> => {

                    // Add the remaining results to those collected in the first step, then return the whole set
                    combinedResults.forEach((value: SearchResponse<M>) => {

                        results.push(...value.results);
                    });

                    return results;
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Performs an Search Request Action with the provided request against the provided basePath
     */
    search$<M extends IValidatedModel<M>, F extends IValidatedModel<F>>(
        request: SearchRequest<F>,
        basePath: string
    ): Observable<SearchResponse<M>> {

        try {
            this._preprocessRequest(request);

            return this._httpClient.post(
                this._getUrl(request, basePath),
                request.serialize()
            ).pipe(
                map((result: unknown) => {

                    return SearchResponse.deserialize<M>(result);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Submits a payload.
     *
     * @param request - The submit request wrapping the payload to send
     * @param basePath - The base path segment for the submit endpoint
     * @param serializeOptions - Options passed through to `request.serialize()`. Defaults to
     * fully redacted serialization (safe for the overwhelming majority of callers). Callers
     * MUST NOT default this to `{ includeRedactedFields: true }` for every caller of `submit$` —
     * only pass it when the specific submission being sent legitimately needs to transmit a
     * `@redactedValueField()`-marked value (e.g. a password on a set-password submission). See
     * {@link UserApiClientService.setPassword$} for the one call site that opts in today; do not
     * "simplify" this parameter away, as doing so would silently defeat redaction for every other
     * caller of this shared method.
     * @returns Observable that emits a SubmitResponse
     */
    submit$<S extends IValidatedModel<S>, R extends IValidatedModel<R> | void = void>(
        request: SubmitRequest<S>,
        basePath: string,
        serializeOptions?: ISerializeOptions
    ): Observable<SubmitResponse<R>> {

        try {
            this._preprocessRequest(request);
            return this._httpClient.post(
                this._getUrl(request, basePath),
                request.serialize(serializeOptions)
            ).pipe(
                map((result: unknown) => {

                    return SubmitResponse.deserialize<R>(result);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Performs an Update Request Action with the provided request against the provided basePath
     */
    update$<M extends IValidatedModel<M>, O extends IValidatedModel<O> | void = void>(
        request: UpdateRequest<M, O>,
        basePath: string
    ): Observable<UpdateResponse<M>> {

        try {
            this._preprocessRequest(request);

            return this._httpClient.put(
                this._getUrl(request, basePath),
                request.serialize()
            ).pipe(
                map((result: unknown) => {

                    return UpdateResponse.deserialize<M>(result);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Updates the record of the original entity with the updated model provided
     * @returns the updated record if there is changes
     */
    updateWithChanges$<M extends IValidatedDomainModel<M>, O extends IValidatedModel<O>>(
        original: M, updated: M, basePath: string, options?: O
    ): Observable<M> {

        try {
            if (!original || !updated) {
                throw new InvalidParametersException(`original or updated ${original.modelName} is invalid`);
            }
            if (original.esVersion === undefined || original.esVersion === null) {
                throw new InvalidParametersException(`original ${original.modelName} is missing esVersion`);
            }
            const changes = original.getChanges(updated);
            if (changes.hasChanges) {
                const fields: M = ModelUtil.deserialize(changes.fields, original.modelName, { action: ModelAction.ClientUpdate });
                // do an update request using fields and removeFields from changes and return the payload from the update response
                const updateRequest = RequestUtil.createUpdateRequest<M, O>(
                    ModelUtil.getId(original), ModelAction.ClientUpdate, fields, original.esVersion, changes.removeFields, options
                );
                return this.update$(updateRequest, basePath).pipe(
                    map((response) => response.payload)
                );
            }
            else {
                // There were no changes, no update was necessary
                return of(original);
            }
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error, "Failed to updateWithChanges"));
        }
    }

    /**
     * Performs a Custom Operation Action with the provided request against the provided basePath
     */

    /**
     * Performs a v2 autocomplete POST request to `/${basePath}/autocomplete`.
     *
     * This method handles autocomplete lookups that use `AutocompleteRequest` as the POST body
     * and return an `AutocompleteResponse`. It is intentionally separate from the standard
     * `SearchRequest` envelope used by `search$()` because autocomplete endpoints do not
     * conform to that envelope shape.
     *
     * @param request - The autocomplete request body specifying `subject`, `field`, `text`, and `size`
     * @param basePath - The v2 resource path segment (e.g. `"product"`)
     * @returns Observable that emits an `AutocompleteResponse` containing the list of matching strings
     *
     * @example
     * ```typescript
     * const request = new AutocompleteRequest();
     * request.subject = Product.modelName;
     * request.field = 'brand';
     * request.text = 'org';
     * this.internalApiRequestService.autocomplete$(request, 'product').subscribe(response => {
     *   console.log(response.results); // ['Organic Valley', 'Organix', ...]
     * });
     * ```
     */
    autocomplete$(request: AutocompleteRequest, basePath: string): Observable<AutocompleteResponse> {

        const url = new URL(`/v2/${basePath}/autocomplete`, this._appConfig.apiUrl).toString();

        return this._httpClient.post(url, request.serialize()).pipe(
            map((result: unknown) => AutocompleteResponse.deserialize(result)),
            catchError((error: unknown) => throwError(() => this._errorHandler.handleServiceError(error)))
        );
    }

    customOp$<M extends IValidatedModel<M>, O = void>(
        request: CustomOpRequest<O>, basePath: string
    ): Observable<CustomOpResponse<M>> {

        try {
            this._preprocessRequest(request);

            return this._httpClient.post(
                this._getUrl(request, basePath),
                request.serialize()
            ).pipe(
                map((response: unknown) => {

                    return CustomOpResponse.deserialize<M>(response);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Validates and pre-processes the given request against the given action.
     */
    private _preprocessRequest(request: IRequestModel<unknown, unknown>): void {

        if (!request?.action) {
            throw new ValidationException(`No request was provided`);
        }

        // This ensures any fields required for the request to be valid for that action are set
        request.onAction(request.action);

        const validationResult = request.validate(request.action);

        if (!validationResult.valid) {
            throw new ValidationException(`Request was invalid: ${validationResult.errors.join(", ")}`);
        }
    }

    /**
     * Returns the url for the request. If the request requires query or path params
     */
    private _getUrl(request: IRequestModel<unknown, unknown>, basePath: string): string {

        let path = `/v2/${basePath}`;
        if (request.urlPath) {
            path = path + request.urlPath;
        }
        const url = new URL(path, this._appConfig.apiUrl);

        return url.toString();
    }
}
